import {
  filterPathIntersection,
  filterPathIntersectionCached,
  following_are_optionals,
  sortForSortierung,
} from "oc-app/metadata/GraphMetadata";
import { MetaProxy } from "oc-app/metadata/PathTypes";
import { findAuswahl } from "./elementFunctions";
import { agt } from "./Arrangement";
import * as Commons from "oc-commons/Commons";
import { maxFrequent } from "oc-commons/Commons";
import _, { uniq } from "lodash";
import { isAufgeschraubt } from "./pfostenFunctions";
import { Bag } from "oc-commons/Bag2";
import { MAuswahlwert, MPfosten, MUMProfil } from "../../metamodell";
import { PAuswahlwert, PElement, PPfosten, PSerie } from "../../metamodell_paths";
import { Pfosten } from "./Pfosten";
import { ElementAuswahlAccessor } from "./ElementAuswahlAccessoren";

export type PfostenAuswahlAccessorKey =
  | "Pfostenfarbe"
  | "Pfostenart"
  | "Bodenbefestigung"
  | "Verbundanker"
  | "Montageadapter"
  | "Elementhalter"
  | "Ummantelung"
  | "Pfostenkappe";

function getBestAuswahlwertForKey(
  getterAuswahlwert,
  getterIndividualisiert,
  mostRecentAuswahlwert
) {
  {
    // Respektiere für die Anzeige die zuletzt gewählte Pfostenart, solange sie gesetzt ist
    if (mostRecentAuswahlwert != null) {
      return mostRecentAuswahlwert;
    }
    const pfosten = agt().getSelectedOrAllPfosten();
    // Nimm den globalen Auswahlwert, falls selektiert und wenigstens ein Pfosten diesem entspricht
    const defaultValue = getterAuswahlwert(agt());
    const hasSelectedPfosten = agt().selectedObjects.some((p) => p.isPfosten);
    if (hasSelectedPfosten && pfosten.some((p) => getterAuswahlwert(p) === defaultValue)) {
      return defaultValue;
    }
    // Sonst nimm den Auswahlwert, der am häufigsten vorkommt, bevorzuge aber im Zweifel den defaultValue
    const bag = new Bag();
    bag.inc(defaultValue, 0.6);
    for (const p of pfosten) {
      const auswahlwert = getterAuswahlwert(p);
      const individualisiert = getterIndividualisiert(p);
      const score = (p.isTorpfosten ? 0.5 : 1) * (individualisiert ? 0.1 : 1);
      bag.inc(auswahlwert, score);
    }
    return bag.getMaxKey();
  }
}

export function filterPfostenAuswahlAccessorenForMany(
  pfosten: Pfosten[]
): Record<PfostenAuswahlAccessorKey, ElementAuswahlAccessor> {
  let einzelnerPfostenSelected = pfosten.length === 1 ? pfosten[0] : null;
  if (APP.isTorkonfigurator)
    einzelnerPfostenSelected = APP.arrangement.getPfosten().find((p) => !p.isUnechterpfosten);

  const pfostenMetas = _.uniq(pfosten.map((p) => p.meta));

  const pathes = pfosten.every((p) => p.isTorpfosten)
    ? _.flattenDeep(
        pfosten.map((p) =>
          p.elemente.filter((e) => e.isTor).map((e) => [e, (e) => e.meta.Pfostenfarbe.Auswahlwert])
        )
      )
    : [
        pfosten.flatMap((p) => p.elemente),
        (p: MetaProxy<PElement>) => p.meta.Pfostenfarbe.Auswahlwert,
      ];

  const isTorpfosten = (pfosten) =>
    pfosten?.elemente
      .filter((e) => e.isTor)
      .map((e) => e.getTorpfosten().filter((p) => p !== null))
      .flat().length > 0;

  const getPfostenartPathes = (pfosten) =>
    isTorpfosten(pfosten)
      ? [
          ...pfosten.elemente
            .filter((e) => e.isTor)
            .map((e) => e.getTorpfosten().map((p) => p?.elemente.map((e) => e.meta.Pfostentyp)))
            .flat(2)
            .map((p) => [
              p,
              (pfostentyp) =>
                pfostentyp.Pfosten.Pfostenart.PfostenartRelation.Pfostenart.Auswahlwert,
            ])
            .flat(),
        ]
      : [
          ...pfosten.elemente
            .map((e) => e.meta.Pfostentyp)
            .map((pfostentypen) =>
              pfostentypen.filter((pfostentyp) => Commons.isEmpty(pfostentyp.Tor))
            )
            .map((p) => [p, (pfostentyp) => pfostentyp.Pfosten.Pfostenart.Auswahlwert])
            .flat(),
        ];

  const auswahlen = [
    {
      key: "Pfostenfarbe",
      auswahlwerte:
        einzelnerPfostenSelected && einzelnerPfostenSelected?.isUMProfil
          ? uniq(
              APP.metadata.UMProfil.filter(
                (umProfil) => umProfil.Typ === (einzelnerPfostenSelected.meta as MUMProfil).Typ
              )
                .map((umProfil) => umProfil.Pfostenfarbe?.Auswahlwert)
                .filter((a) => a != null)
            )
          : (filterPathIntersection(
              pfostenMetas,
              (p: PPfosten) => p.Pfostenmass.Pfosten.Pfostenfarbe.Auswahlwert,
              pfostenMetas,
              (p: PPfosten) => p.Pfostentyp.Pfosten.Pfostenfarbe.Auswahlwert,
              pfostenMetas,
              (p: PPfosten) => p.DetailSerie.Pfosten.Pfostenfarbe.Auswahlwert,
              APP.arrangement.serie,
              (serie: PSerie) => serie.Pfostentyp.Pfosten.Pfostenfarbe.Auswahlwert,
              following_are_optionals,
              pfostenMetas,
              (p: PPfosten) => p.Pfostenart.Pfosten.Pfostenfarbe.Auswahlwert,
              //Frage ggf auch das zugehörige Tor: Pfosten.elemente enthält Tor
              // @ts-ignore
              ...pathes
            ) as MAuswahlwert[]),
      getter: () => agt().pfostenfarbeAuswahlwert,
      setter: (pfostenfarbeAuswahlwert) => agt().changeSelectedPfosten({ pfostenfarbeAuswahlwert }),
    },
    {
      key: "Pfostenart",
      pathToZulässigeAuswahlwerte: (einzelnerPfostenSelected
        ? getPfostenartPathes(einzelnerPfostenSelected)
        : [
            agt()
              .getPfosten()
              .map((p) => getPfostenartPathes(p))
              .map((path) =>
                filterPathIntersection(
                  // @ts-ignore
                  ...path
                )
              )
              .flat(),
            (p) => p,
          ]
      ).concat(APP.arrangement.serie, (serie) => serie.Pfostentyp.Pfosten.Pfostenart.Auswahlwert),
      getter: () =>
        getBestAuswahlwertForKey(
          (p) =>
            !Commons.isEmpty(p.pfostenartAuswahlwert?.Objekt?.[0]?.PfostenartRelation)
              ? p.pfostenartAuswahlwert.Objekt[0].PfostenartRelation[0].Pfostenart.Auswahlwert
              : p.pfostenartAuswahlwert,
          (p) => p.pfostenartAuswahlwertIndividualisiert,
          agt().mostRecentPfostenart
        ),

      setter: (pfostenartAuswahlwert) => agt().changeSelectedPfosten({ pfostenartAuswahlwert }),
    },
    {
      key: "Bodenbefestigung",
      pathToZulässigeAuswahlwerte: (() => {
        const einbetoniertAuswahlwert = APP.richMetadata.findAuswahlwert("Einbetoniert im Boden");
        return (
          (einzelnerPfostenSelected
            ? [
                ...einzelnerPfostenSelected.elemente
                  .map((e) =>
                    !einzelnerPfostenSelected.isAufschraubpfostenMöglich
                      ? [einbetoniertAuswahlwert, (auswahlwert) => auswahlwert]
                      : [e, (element) => element.meta.Bodenbefestigung.Auswahlwert]
                  )
                  .flat(),
              ]
            : [
                agt()
                  .getPfosten()
                  .filter((p) => p.isAufschraubpfostenMöglich),
                (p) => p.elemente.meta.Bodenbefestigung.Auswahlwert,
              ]) as any[]
        ).concat(
          APP.arrangement.serie,
          (serie: PSerie) => serie.Pfostentyp.Pfosten.Bodenbefestigung.Auswahlwert
        );
      })(),
      getter: () =>
        getBestAuswahlwertForKey(
          (p) => p.bodenbefestigungAuswahlwert,
          (p) => p.bodenbefestigungAuswahlwertIndividualisiert,
          agt().mostRecentBodenbefestigung
        ),
      setter: (bodenbefestigungAuswahlwert) =>
        agt().changeSelectedPfosten({ bodenbefestigungAuswahlwert }),
    },
    {
      key: "Verbundanker",
      accept:
        !einzelnerPfostenSelected?.isUnechterpfosten &&
        (einzelnerPfostenSelected?.isAufgeschraubt ||
          isAufgeschraubt(agt().bodenbefestigungAuswahlwert)) &&
        (!einzelnerPfostenSelected || pfosten?.some((s) => s.verbundankerAuswahlwert)),
      getter: () =>
        maxFrequent(
          agt()
            .getSelectedOrAllPfosten()
            .map((e) => e.verbundankerAuswahlwert)
        ) ?? APP.richMetadata.getAuswahlWertVoreinstellung(APP.metadata.Verbundanker_Auswahlwert),
      setter: (verbundankerAuswahlwert) => agt().changeSelectedPfosten({ verbundankerAuswahlwert }),
    },
    {
      key: "Montageadapter",
      accept:
        !einzelnerPfostenSelected?.isUnechterpfosten &&
        (einzelnerPfostenSelected?.isAufgeschraubt ||
          isAufgeschraubt(agt().bodenbefestigungAuswahlwert)) &&
        (!einzelnerPfostenSelected || pfosten?.some((s) => s.montageadapterAuswahlwert)),
      getter: () =>
        maxFrequent(
          agt()
            .getSelectedOrAllPfosten()
            .map((e) => e.montageadapterAuswahlwert)
        ) ?? APP.richMetadata.getAuswahlWertVoreinstellung(APP.metadata.Montageadapter_Auswahlwert),
      setter: (montageadapterAuswahlwert) =>
        agt().changeSelectedPfosten({ montageadapterAuswahlwert }),
    },
    {
      key: "Elementhalter",
      pathToZulässigeAuswahlwerte: einzelnerPfostenSelected
        ? [
            einzelnerPfostenSelected.elemente,
            (e: MetaProxy<PElement>) => e.meta.Elementhalter.Auswahlwert,
            einzelnerPfostenSelected,
            (e: MetaProxy<PPfosten>) => e.meta.Elementhalter.Auswahlwert,
          ]
        : [
            agt()
              .getPfosten()
              .flatMap((p) => p.elemente),
            (p: MetaProxy<PElement>) => p.meta.Elementhalter.Auswahlwert,
            agt().getPfosten(),
            (p: MetaProxy<PPfosten>) => p.meta.Elementhalter.Auswahlwert,
          ],
      getter: () => agt().elementhalterAuswahlwert,
      setter: (elementhalterAuswahlwert: MAuswahlwert) =>
        agt().changeSelectedPfosten({ elementhalterAuswahlwert }),
    },
    {
      key: "Ummantelung",
      accept:
        !einzelnerPfostenSelected?.isUnechterpfosten &&
        !agt()
          .getSelectedOrAllPfosten()
          .every((s) => Commons.isEmpty((s?.meta as MPfosten)?.Ummantelung)),
      getter: () =>
        maxFrequent(
          agt()
            .getSelectedOrAllPfosten()
            .map((e) => e.ummantelungAuswahlwert)
        ) ?? APP.richMetadata.getAuswahlWertVoreinstellung(APP.metadata.Ummantelung_Auswahlwert),
      setter: (ummantelungAuswahlwert: MAuswahlwert) =>
        agt().changeSelectedPfosten({ ummantelungAuswahlwert }),
    },
    {
      key: "Pfostenkappe",
      accept:
        !einzelnerPfostenSelected?.isUnechterpfosten &&
        !agt()
          .getSelectedOrAllPfosten()
          .every((s) => Commons.isEmpty((s?.meta as MPfosten)?.Pfostenkappe)),
      pathToZulässigeAuswahlwerte: [
        agt()
          .getSelectedOrAllPfosten()
          .filter((p) => !p?.isUnechterpfosten),
        (p: MetaProxy<PPfosten>) => p.meta.Pfostenkappe.Auswahlwert,
      ],
      getter: () =>
        maxFrequent(
          agt()
            .getSelectedOrAllPfosten()
            .map((e) => e.pfostenkappeAuswahlwert)
        ) ?? APP.richMetadata.getAuswahlWertVoreinstellung(APP.metadata.Pfostenkappe_Auswahlwert),
      setter: (pfostenkappeAuswahlwert: MAuswahlwert) =>
        agt().changeSelectedPfosten({ pfostenkappeAuswahlwert }),
    },
  ];

  const auswahlAccessoren = auswahlen.map(
    ({ key, accept, auswahlwerte, pathToZulässigeAuswahlwerte, getter, setter }) => {
      const auswahl = findAuswahl(key);
      if (auswahl == null) {
        throw new Error(`missing Auswahl with key ${key} from db`);
      }
      const auswahlwert = getter();

      auswahlwerte =
        auswahlwerte ??
        APP.metadata.Auswahlwert.filter((w) => w.Auswahl.Key == auswahl.Key).filter(
          (a) => !a.Gesperrt
        );

      const möglicheAuswahlwerte: MAuswahlwert[] =
        accept ?? true
          ? (pathToZulässigeAuswahlwerte ?? []).length > 0
            ? filterPathIntersectionCached("Auswahl.tsx:129")(
                auswahlwerte,
                (a: PAuswahlwert) => a,
                // @ts-ignore
                ...pathToZulässigeAuswahlwerte
              )
            : auswahlwerte
          : [];

      return {
        key,
        auswahl,
        auswahlwert,
        möglicheAuswahlwerte,
        setter,
      };
    }
  );

  const accessoren = {
    ..._.keyBy(auswahlAccessoren, "key"),
  };
  Object.values(accessoren).forEach(
    (d) => (d.möglicheAuswahlwerte = sortForSortierung(d.möglicheAuswahlwerte))
  );
  return accessoren as unknown as Record<PfostenAuswahlAccessorKey, ElementAuswahlAccessor>;
}
